import React, { useEffect } from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { ChevronIcon, CloseIcon, KeyIcon } from "./icons";
import { InviteMemberModalProps } from "./types";
import { Select } from "../Select";

/** Invite member modal: a pure view of the email and, where the product has them, the role and the app access. */
export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  title = "Invite a new team member",
  subtitle = "Link an account",
  email,
  onEmailChange,
  emailPlaceholder = "name@company.com",
  emailReadOnly = false,
  role = "",
  onRoleChange,
  roleOptions = [],
  rolePlaceholder = "Role name",
  scoped = false,
  onScopedChange,
  accounts = [],
  accountId = null,
  onAccountChange,
  accountPlaceholder = "Choose an account",
  apps = [],
  selectedAppIds = [],
  onAppToggle,
  appsLoading = false,
  appsHint,
  submitLabel = "Send invite",
  submitting = false,
  canSubmit = true,
  onSubmit,
  onClose,
  className,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Escape closes an open dropdown first, not the whole modal.
      if (e.key === "Escape" && !document.querySelector('[role="listbox"]'))
        onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        className={cs(styles.modal, className)}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <span className={styles.icon}>
            <KeyIcon />
          </span>
          <div>
            <h2 className={styles.title}>{title}</h2>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
          <button
            type="button"
            className={styles.close}
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        {/* A form so that Enter in the email field sends the invitation, the way it did before the modal. */}
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit && !submitting) onSubmit();
          }}
        >
          <div className={styles.body}>
            <label className={styles.field}>
              <span className={styles.label}>Email</span>
              <input
                className={styles.control}
                type="email"
                placeholder={emailPlaceholder}
                value={email}
                autoFocus={!emailReadOnly}
                readOnly={emailReadOnly}
                onChange={(e) => onEmailChange(e.target.value)}
              />
            </label>

            {onRoleChange && roleOptions.length > 0 && (
              <div className={styles.field}>
                <span className={styles.label}>Role</span>
                <Select
                  value={role}
                  options={roleOptions}
                  onChange={onRoleChange}
                  placeholder={rolePlaceholder}
                  aria-label="Role"
                />
              </div>
            )}

            {onScopedChange && (
              <div className={styles.field}>
                <span className={styles.label}>App Access</span>

                <label
                  className={cs(styles.option, {
                    [styles.optionActive]: !scoped,
                  })}
                >
                  <input
                    type="radio"
                    name="appAccess"
                    checked={!scoped}
                    onChange={() => onScopedChange(false)}
                  />
                  All Apps
                </label>

                <div
                  className={cs(styles.option, styles.optionGroup, {
                    [styles.optionActive]: scoped,
                  })}
                >
                  <label className={styles.optionHead}>
                    <input
                      type="radio"
                      name="appAccess"
                      checked={scoped}
                      onChange={() => onScopedChange(true)}
                    />
                    Selected Apps
                    <ChevronIcon up={scoped} />
                  </label>

                  {scoped && (
                    <div className={styles.appList}>
                      {accounts.length > 1 && (
                        <Select
                          value={accountId ?? ""}
                          options={accounts.map((acc) => ({
                            value: acc.id,
                            label: acc.name,
                          }))}
                          onChange={(v) => onAccountChange?.(v || null)}
                          placeholder={accountPlaceholder}
                          aria-label="Account"
                        />
                      )}

                      {appsLoading && (
                        <p className={styles.hint}>Loading apps…</p>
                      )}
                      {!appsLoading && appsHint && (
                        <p className={styles.hint}>{appsHint}</p>
                      )}

                      {!appsLoading &&
                        apps.map((app) => (
                          <label key={app.id} className={styles.appRow}>
                            <input
                              type="checkbox"
                              checked={selectedAppIds.includes(app.id)}
                              onChange={(e) =>
                                onAppToggle?.(app.id, e.target.checked)
                              }
                            />
                            {app.iconUrl ? (
                              <img
                                className={styles.appIcon}
                                src={app.iconUrl}
                                alt=""
                              />
                            ) : (
                              <span
                                className={styles.appIcon}
                                aria-hidden="true"
                              />
                            )}
                            <span className={styles.appName}>{app.name}</span>
                          </label>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            type="submit"
            className={styles.submit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? "Sending…" : submitLabel}
          </button>
        </form>
      </div>
    </div>
  );
};

export default InviteMemberModal;
